import React from 'react'
import { Box, Stack, InputGroup, InputLeftElement, Icon, Input, HStack, useToast, Button, IconButton, Menu, MenuButton, MenuList, Container, MenuItem } from '@chakra-ui/react'
import { FiMenu, FiRefreshCw, FiSearch } from 'react-icons/fi'
import { ProviderTable } from './ProviderTable'
import { TelemetryClientService } from '../services/TelemetryClientService'
import { IProvider } from '@/shared/IProvider'
import { NewProviderDialog } from './NewProviderDialog'
import { EditProviderDialog } from './EditProviderDialog'
import { AuthService } from '../services/AuthService'
import { AppContext } from '../comments/AppContext'
import { CourseProviderStorage } from '../services/CourseProviderStorage'
import { stringifyError } from '@/shared/parse'
import { StorageService } from '../services/StorageService'
import { MenuItemOpenSharePoint } from '../course/MenuItemOpenSharePoint'
import { useFilterText } from './filters/useFilterText'
import { DeleteConfirmationDialog } from './DeleteConfirmationDialog'
import { AppToolbar } from '../navbar/AppToolbar';
import { TableCounterBox } from './TableCounterBox'

export const ProviderTableFrame = (props: {
  errorMessage?: any, setErrorMessage: (error: any) => void,
}) => {

  TelemetryClientService.page('ProviderTableFrame');
  const [isLoading, setIsLoading] = React.useState(true);

  const toast = useToast({
    duration: 3000,
  });

  const onSendInvitation = async (provider: IProvider) => {
    toast.promise(CourseProviderStorage.inviteProvider(provider.id), {
      loading: { title: `Sending invitation to ${provider.ProviderEmail}...` },
      success: { title: 'The invitation has been sent.' },
      error: err => ({ title: 'Unable to send the invitation', description: stringifyError(err) }),
    });
  }

  const { isAuthenticated } = React.useContext(AppContext);

  const loadProviders = (force?: boolean) => StorageService.loadItems(CourseProviderStorage.getProviders, props.setErrorMessage, setIsLoading, force).then(setAllProviders);

  React.useEffect(() => {
    if (AuthService.isStaff) {
      loadProviders(false);
    }
  }, [isAuthenticated]);

  const [filterText, setFilterText, filterWords] = useFilterText('eitum-providers-table-filter-text');

  const [allProviders, setAllProviders] = React.useState<IProvider[]>([]);

  const providers = React.useMemo(() => {
    return allProviders.filter(p => {
      if (filterWords.length > 0) {
        return filterWords.every(w => [p.ProviderName, p.ProviderIdentifier, p.ProviderEmail, p.ProviderContact, p.ProviderPhone, p.ProviderAddress, p.ProviderWebsite].some(t => t?.toLowerCase().includes(w)))
      }
      return true;
    });
  }, [allProviders, filterWords]);

  const [selectedProvider, setSelectedProvider] = React.useState<IProvider>();
  const [providerToDelete, setProviderToDelete] = React.useState<IProvider>();

  const onAddNewProvider = async (created: IProvider) => {
    setAllProviders(old => [...old, created]);
  }

  const onShowNewProviderDialog = () => {
    setIsNewProviderDialogOpen(true);
  }

  const onSaveProvider = async (provider: IProvider, invite: boolean) => {
    const updated = await CourseProviderStorage.patchProvider(provider, invite);
    setAllProviders(old => old.map(p => p.id === updated.id ? updated : p));
  }

  const onEditProvider = async (provider: IProvider) => {
    setSelectedProvider(provider);
  }

  const onDeleteProvider = async (provider: IProvider) => {
    setProviderToDelete(provider);
  }

  const onConfirmDeleteProvider = async (provider: IProvider) => {
    await CourseProviderStorage.deleteProvider(provider.id);
    setAllProviders(p => p.filter(c => c.id !== provider.id));
  }

  const [isNewProviderDialogOpen, setIsNewProviderDialogOpen] = React.useState(false);

  return (
    <>
      <AppToolbar>
        <HStack>
          <Button hideBelow='md' variant='ghost' isDisabled={isLoading || !!props.errorMessage} onClick={onShowNewProviderDialog} size="md" >Add New Provider...</Button>
          <IconButton hideBelow='md' onClick={() => loadProviders(true)} aria-label="Refresh" isDisabled={isLoading} variant='ghost' icon={<Icon as={FiRefreshCw} />} />
          <Menu>
            <MenuButton as={IconButton} isDisabled={isLoading || !AuthService.isAdmin} variant='ghost' icon={<FiMenu />}>Actions</MenuButton>
            <MenuList>
              <MenuItem hideFrom='md' isDisabled={isLoading || !!props.errorMessage} onClick={() => loadProviders(true)}>Refresh</MenuItem>
              <MenuItemOpenSharePoint relativeUrl='Lists/Providers' />
              <MenuItem hideFrom='md' isDisabled={isLoading || !!props.errorMessage} onClick={onShowNewProviderDialog}>Add New Provider...</MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      </AppToolbar>
      <Box opacity={isLoading ? 0.5 : undefined} maxW='full'>
        <Stack>
          <Container>
            <HStack justify="space-between">
              <Stack direction={{ base: 'column', md: 'row' }} justify="space-between">
                <HStack spacing="3" justify='start'>
                  <InputGroup maxW="xs">
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FiSearch} color="fg.muted" boxSize="5" />
                    </InputLeftElement>
                    <Input placeholder="Search" value={filterText} onChange={evt => setFilterText(evt.target.value)} />
                  </InputGroup>
                  {/* <Divider orientation='vertical' /> */}
                </HStack>
              </Stack>
              <TableCounterBox isLoading={isLoading} count={providers.length}  label='provider(s)' />
            </HStack>
          </Container>
          <Box overflowX="auto">
            <ProviderTable
              filter={filterWords}
              providers={providers}
              onDeleteProvider={onDeleteProvider}
              onSendInvitation={onSendInvitation}
              onEditProvider={onEditProvider}
            />
          </Box>
        </Stack>
        {isNewProviderDialogOpen && <NewProviderDialog
          onClose={() => setIsNewProviderDialogOpen(false)}
          onSave={onAddNewProvider}
        />}
        {!!selectedProvider && <EditProviderDialog
          onClose={() => setSelectedProvider(undefined)}
          provider={selectedProvider}
          onSaveProvider={onSaveProvider}
        />}
        {!!providerToDelete && <DeleteConfirmationDialog
          title='Delete Provider'
          description={`The provider "${providerToDelete?.ProviderName}" (${providerToDelete?.ProviderIdentifier}) will be moved to the site recycle bin. Only providers that have no courses can be deleted. Do you want to proceed?`}
          data={providerToDelete}
          onDelete={onConfirmDeleteProvider}
          onClose={() => setProviderToDelete(undefined)}
        />}
      </Box>
    </>
  )
}